import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, TextInput, SelectInput } from "@/components/FormKit";
import { visitsApi, apiErrorMessage, isSuppressionOverride } from "@/api/client";
import { OverrideNotice } from "@/components/forms/OverrideNotice";
import { toast } from "sonner";

export function VisitPlanDialog({ open, onOpenChange, orgId, locations = [], contacts = [], onSaved }) {
  const [form, setForm] = useState({ location_id: "", contact_id: "", planned_at: "", visit_type: "field" });
  const [override, setOverride] = useState("");
  const [needsOverride, setNeedsOverride] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ location_id: locations[0]?.id || "", contact_id: "", planned_at: "", visit_type: "field" });
      setOverride(""); setNeedsOverride(false); setError("");
    }
  }, [open, locations]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.location_id) { setError("A visit needs a location"); return; }
    if (!form.planned_at) { setError("Choose a planned time"); return; }
    setSaving(true); setError("");
    const payload = {
      location_id: form.location_id, contact_id: form.contact_id || null,
      planned_at: new Date(form.planned_at).toISOString(), visit_type: form.visit_type,
    };
    if (needsOverride) payload.override_reason = override;
    try {
      const res = await visitsApi.create(orgId, payload);
      toast.success("Visit planned");
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (err) {
      if (isSuppressionOverride(err)) { setNeedsOverride(true); setError(""); }
      else setError(apiErrorMessage(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="visit-plan-dialog">
        <DialogHeader><DialogTitle className="font-heading">Plan visit</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Location">
            <SelectInput testid="visit-location" value={form.location_id} onChange={set("location_id")}>
              <option value="">Choose a venue…</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </SelectInput>
          </Field>
          {locations.length === 0 && <p className="text-xs text-amber-700">Add a location to this organisation first.</p>}
          <Field label="Contact (optional)">
            <SelectInput testid="visit-contact" value={form.contact_id} onChange={set("contact_id")}>
              <option value="">—</option>
              {contacts.map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}
            </SelectInput>
          </Field>
          <Field label="Planned time"><TextInput testid="visit-planned-at" type="datetime-local" value={form.planned_at} onChange={set("planned_at")} /></Field>
          <Field label="Visit type">
            <SelectInput testid="visit-type" value={form.visit_type} onChange={set("visit_type")}>
              <option value="field">Field (out visiting)</option>
              <option value="phone">Phone</option>
              <option value="email">Email</option>
            </SelectInput>
          </Field>
          {needsOverride && <OverrideNotice value={override} onChange={setOverride} entity="visit" />}
          {error && <p data-testid="visit-plan-error" className="text-sm font-medium text-rose-600">{error}</p>}
          <button type="submit" disabled={saving || (needsOverride && !override.trim())} data-testid="visit-plan-submit"
            className="min-h-[48px] w-full rounded-lg bg-teal-700 font-semibold text-white hover:bg-teal-800 disabled:opacity-60">
            {saving ? "Saving…" : needsOverride ? "Proceed with override" : "Plan visit"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
